<template>
  <div class="modal-content">
    <div class="card kpi-accent" :style="{ '--accent': detectionZoneColor[detectionZone.type] || '#4F7EB3' }">
      <!-- Modal Header -->
      <div class="card-header modal-head">
        <div class="head-left">
          <h5 class="card-title">
            {{ $t('zoneSettings.settings.title') }} — <span class="dz-name">{{ detectionZone.name }}</span>
          </h5>
          <div class="zone-type-line">
            <label class="form-label-type">{{ $t('zoneSettings.settings.zoneTypeLabel') }}</label>
            <span
              class="zone-dot"
              :style="{ background: detectionZoneColor[detectionZone.type] || '#4F7EB3' }"
            ></span>
            <span class="zone-type-text">{{ zoneTypeLabel(detectionZone.type) }}</span>
          </div>
        </div>
      </div>

      <!-- Modal Body -->
      <div class="card-body modal-body">
        <!-- How Allen sees this zone — derive-then-confirm context. Edits bind into
             the settings blob and persist with the Save button below (not instantly). -->
        <!-- Shown while DRAWING too, not only on a later visit. The id only exists
             once the flow is saved, so keying the panel on it hid this question at
             exactly the moment someone is looking at the camera image and can answer
             it — and a return visit is precisely when nobody does. Without an id the
             schema and guesses are asked for by type + name instead. -->
        <ZoneCoachContext v-if="backendZoneId || coachContextZoneTypeId"
                          :zone-id="backendZoneId"
                          :zone-type-id="coachContextZoneTypeId"
                          :zone-name="detectionZone.name"
                          :geometry="zoneHasPolygon ? 'polygon' : 'tripwire'"
                          v-model="detectionZoneSettings.coachContext" />

        <!-- Staff-side linking: Cash Register, and since phase 3 the Info / Service /
             Pick Up desk zones too. Every service counter is three zones and the STAFF
             one carries the pairing. docs/reference/service_response_measurement.md §28.4 -->
        <hr v-if="isStaffZone && zoneHasPolygon" class="divider-soft" />
        <div v-if="isStaffZone && zoneHasPolygon" class="block">
          <label class="form-label">{{ linkedZoneLabel }}</label>
          <p class="field-help">{{ linkedZoneHelp }}</p>
          <!-- Bound to the zone ID, not the name. The NAME is still written alongside
               it (see the watcher in setup) because the edge firmware reads that field
               — but the portal's own link is the id, so renaming a zone can no longer
               silently break the pairing.
               docs/reference/service_response_measurement.md §12 -->
          <select class="form-select" v-model="detectionZoneSettings.LinkedDetectionZoneId">
            <option :value="null">{{ $t('zoneSettings.common.none') }}</option>
            <option
              v-for="zone in interactionZonesForFlow"
              :key="zone.DetectionZoneId"
              :value="zone.DetectionZoneId"
            >
              {{ zone.DetectionZoneName }}
            </option>
          </select>
          <p v-if="!interactionZonesForFlow.length" class="field-help">
            {{ $t('zoneSettings.settings.noCounterpart', { name: zoneTypeLabel(linkedCounterpartName) }) }}
          </p>
        </div>

        <!-- Group divider: "General Settings" — always shown. Hosts settings
             that apply equally to polygon counting and tripwire crossings
             (the Track ID Cleanup Rules below). Sits above the geometry-
             specific "Zone Settings" / "Tripwire Settings" sections. -->
        <div class="settings-section-header">{{ $t('zoneSettings.settings.generalSettings') }}</div>

        <!-- Card: Track ID Cleanup Rules — shared between polygon and
             tripwire counting. Three controls always; Split Visit Merge
             only when the zone has a polygon (it describes merging
             adjacent polygon visits — doesn't apply to tripwire crossings). -->
        <div class="subcard" style="margin-top: 0px;">
          <div class="subcard-head">
            <h6 class="subcard-title">{{ $t('zoneSettings.settings.cleanupRules') }}</h6>
          </div>
          <div class="subcard-body">
            <div class="block">
              <label class="form-label">{{ $t('zoneSettings.settings.debounce') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.debounceHelp') }}</p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.DebounceWritingThreshold"
                  :min="0"
                  :max="5"
                  :step="0.1"
                  :format="formatDebounceTooltip"
                />
              </div>
            </div>

            <div class="block">
              <label class="form-label">{{ $t('zoneSettings.settings.expiry') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.expiryHelp') }}</p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.ThresholdRemoveId"
                  :min="0"
                  :max="200"
                  :step="1"
                />
              </div>
            </div>

            <!-- Merge + plausibility group. All three knobs act at match time in
                 the nightly pipeline and all three change visit counts, so they
                 live together. Each is default-or-custom: choosing "default"
                 removes the key from the settings blob entirely so the backend
                 keeps resolving its own per-zone-type value — that way improving
                 a default still reaches every zone that never overrode it.
                 Shown for any zone with a polygon, INCLUDING entrance-like types:
                 the backend applies all three there too, and the entrance cap
                 (5 min) is the tightest one in the table. -->
            <template v-if="zoneHasPolygon">
              <div class="rule-group-label">{{ $t('zoneSettings.settings.mergingSplit') }}</div>

              <div class="block">
                <label class="form-label">{{ $t('zoneSettings.change.splitMerge') }}</label>
                <p class="field-help">
                  {{ $t('zoneSettings.settings.splitMergeHelp') }}
                </p>
                <div class="radio-row">
                  <label class="form-check form-check-inline">
                    <input type="radio" class="form-check-input" id="splitmerge-default"
                           value="default" v-model="splitMergeMode" />
                    <span class="form-check-label">{{ $t('zoneSettings.settings.default', { value: splitMergeDefaultLabel }) }}</span>
                  </label>
                  <label class="form-check form-check-inline">
                    <input type="radio" class="form-check-input" id="splitmerge-custom"
                           value="custom" v-model="splitMergeMode" />
                    <span class="form-check-label">{{ $t('zoneSettings.common.custom') }}</span>
                  </label>
                </div>
                <div class="slider-container" v-if="splitMergeMode === 'custom'">
                  <Slider
                    class="slider"
                    v-model="detectionZoneSettings.SplitVisitMergeThreshold"
                    :min="0"
                    :max="60"
                    :step="1"
                  />
                </div>
              </div>

              <div class="block">
                <label class="form-label">{{ $t('zoneSettings.change.reidMerge') }}</label>
                <p class="field-help">
                  {{ $t('zoneSettings.settings.reidHelp') }}
                </p>
                <div class="radio-row">
                  <label class="form-check form-check-inline">
                    <input type="radio" class="form-check-input" id="reidmerge-default"
                           value="default" v-model="reidMergeMode" />
                    <span class="form-check-label">
                      {{ $t('zoneSettings.settings.defaultFor', { type: zoneTypeLabel(detectionZone.type), value: reidMergeDefaultLabel }) }}
                    </span>
                  </label>
                  <label class="form-check form-check-inline">
                    <input type="radio" class="form-check-input" id="reidmerge-custom"
                           value="custom" v-model="reidMergeMode" />
                    <span class="form-check-label">{{ $t('zoneSettings.common.custom') }}</span>
                  </label>
                </div>
                <div class="slider-container" v-if="reidMergeMode === 'custom'">
                  <Slider
                    class="slider"
                    v-model="detectionZoneSettings.ReIDMergeThresholdSec"
                    :min="0"
                    :max="30"
                    :step="0.5"
                    :format="formatDebounceTooltip"
                  />
                </div>
                <!-- Unlike the same-ID merge, this one can destroy real signal: on a
                     product zone a customer legitimately returns to the same shelf. -->
                <p class="field-help" v-if="reidMergeEffectiveSec > 0 && !isCheckoutZoneType">
                  {{ $t('zoneSettings.settings.reidWarn') }}
                </p>
              </div>

              <div class="rule-group-label">{{ $t('zoneSettings.settings.implausible') }}</div>

              <div class="block">
                <label class="form-label">{{ $t('zoneSettings.change.maxPlausible') }}</label>
                <p class="field-help">
                  {{ $t('zoneSettings.settings.maxPlausibleHelpA') }} <em>{{ $t('zoneSettings.settings.maxPlausibleHelpAnd') }}</em> {{ $t('zoneSettings.settings.maxPlausibleHelpB') }}
                </p>
                <div class="radio-row">
                  <label class="form-check form-check-inline">
                    <input type="radio" class="form-check-input" id="maxplaus-default"
                           value="default" v-model="maxPlausibleMode" />
                    <span class="form-check-label">
                      {{ $t('zoneSettings.settings.defaultFor', { type: zoneTypeLabel(detectionZone.type), value: maxPlausibleDefaultLabel }) }}
                    </span>
                  </label>
                  <label class="form-check form-check-inline">
                    <input type="radio" class="form-check-input" id="maxplaus-custom"
                           value="custom" v-model="maxPlausibleMode" />
                    <span class="form-check-label">{{ $t('zoneSettings.common.custom') }}</span>
                  </label>
                </div>
                <div class="input-group narrow" v-if="maxPlausibleMode === 'custom'">
                  <input
                    type="number"
                    class="form-control"
                    v-model.number="maxPlausibleMinutes"
                    :min="1"
                    :max="720"
                    step="1"
                  />
                  <span class="input-group-text">{{ $t('zoneSettings.common.minutes') }}</span>
                </div>
                <!-- Two different reasons a zone can sit at the 24h guard, and they
                     deserve opposite copy. A staff position is uncapped on purpose;
                     warning about it would be scolding the user for our decision. -->
                <p class="field-help" v-if="maxPlausibleMode === 'default' && isStaffPosition">
                  {{ $t('zoneSettings.settings.staffPosition') }}
                </p>
                <p class="field-help" v-else-if="maxPlausibleMode === 'default' && isMaxPlausibleUnclassified">
                  {{ $t('zoneSettings.settings.unclassified') }}
                </p>
              </div>
            </template>

            <div class="block" v-if="detectionZone.type !== 'Cash Register Zone'">
              <label class="form-label">{{ $t('zoneSettings.settings.sameIdLabel') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.sameIdHelp') }}</p>
              <div class="radio-row">
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="multi-false"
                    :value="false"
                    v-model="detectionZoneSettings.SameIdInOutUniqueData"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.common.no') }}</span>
                </label>
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="multi-true"
                    :value="true"
                    v-model="detectionZoneSettings.SameIdInOutUniqueData"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.common.yes') }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Group divider: "Zone Settings" — shown only when the zone has a
             polygon. Symmetric with the "Tripwire Settings" divider further
             down. Polygon-only zone → just "Zone Settings"; tripwire-only
             zone → just "Tripwire Settings"; combined → both. -->
        <div class="settings-section-header" v-if="zoneHasPolygon">{{ $t('zoneSettings.settings.zoneSettings') }}</div>

        <!-- Card: Detection Precision -->
        <div class="subcard" style="margin-top: 0px;" v-if="zoneHasPolygon && !isEntranceLikeZoneType">
          <div class="subcard-head">
            <h6 class="subcard-title">{{ $t('zoneSettings.settings.precision') }}</h6>
          </div>
          <div class="subcard-body">
            <div class="block">
              <label class="form-label">{{ $t('zoneSettings.settings.overlapMethod') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.overlapMethodHelp') }}</p>
              <div class="radio-row">
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="bottom_overlap"
                    value="bottom_overlap"
                    v-model="detectionZoneSettings.DetectionOverlapMode"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.settings.bottomOverlap') }}</span>
                </label>
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="box_overlap"
                    value="box_overlap"
                    v-model="detectionZoneSettings.DetectionOverlapMode"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.settings.boxOverlap') }}</span>
                </label>
              </div>
            </div>

            <div class="block" v-if="detectionZoneSettings.DetectionOverlapMode === 'box_overlap'">
              <label class="form-label">{{ $t('zoneSettings.settings.minBox') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.minBoxHelp') }}</p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.BoundingBoxOverlapPercentage"
                  :min="55"
                  :max="100"
                />
              </div>
            </div>

            <div class="block" v-if="detectionZoneSettings.DetectionOverlapMode === 'bottom_overlap'">
              <label class="form-label">{{ $t('zoneSettings.settings.minBottom') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.minBottomHelp') }}</p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.BottomOverlapPercentage"
                  :min="55"
                  :max="100"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- The "Stay Duration Filters" card that used to sit here (minimum /
             maximum stay-duration sliders) was removed: MinimumDurationThreshold
             and MaximumDurationThreshold were written to the settings blob but no
             backend code ever read them, so the sliders looked functional and did
             nothing. The maximum half is now real, as "Maximum plausible visit
             length" under Track ID Cleanup Rules above (backed by
             MaxPlausibleVisitDurationSec, which the matcher does enforce). The
             minimum half was deliberately not rebuilt — short visits are already
             classified as walk-bys by the quick-pass engagement buckets, which
             labels them without deleting them from the counts. -->

        <!-- Card: Buying Entity -->
        <div class="subcard" v-if="zoneHasPolygon && !isEntranceLikeZoneType && detectionZone.type !== 'Cash Register Zone'">
          <div class="subcard-head">
            <h6 class="subcard-title">{{ $t('zoneSettings.settings.buyingEntity') }}</h6>
          </div>
          <div class="subcard-body">
            <div class="block">
              <label class="form-label">{{ $t('zoneSettings.settings.buyingEntityLabel') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.buyingEntityHelp') }}</p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.PeopleAmountConvertedToVisits"
                  :min="1"
                  :max="10"
                  :step="1"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Card: Buying Intent (Product/Demo) -->
        <div
          class="subcard subcard-buying-intent"
          v-if="zoneHasPolygon && !isEntranceLikeZoneType && detectionZone.type !== 'Cash Register Zone' && !(detectionZone.type == 'Checkout Waiting Zone' || detectionZone.type == 'Checkout Interaction Zone')"
        >
          <div class="subcard-head">
            <h6 class="subcard-title">{{ $t('zoneSettings.settings.buyingIntent') }}</h6>
          </div>
          <div class="subcard-body">
            <div class="block" v-if="detectionZone.type == 'Product Zone' || detectionZone.type == 'Demo Zone'">
              <label class="form-label">{{ $t('zoneSettings.settings.conversionRate') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.conversionRateHelp') }}</p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.EngagersConversionRate"
                  :min="0"
                  :max="100"
                  :step="1"
                />
              </div>
            </div>

            <div class="block" v-if="detectionZone.type == 'Product Zone' || detectionZone.type == 'Demo Zone'">
              <label class="form-label">{{ $t('zoneSettings.settings.productValue') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.productValueHelp') }}</p>
              <div class="input-group narrow">
                <span class="input-group-text">€</span>
                <input
                  type="number"
                  class="form-control"
                  v-model="detectionZoneSettings.AverageProductValue"
                  min="0"
                  step="1"
                  placeholder="100"
                />
              </div>
            </div>

            <!-- ===== Sector this zone behaves like =====
                 Pre-populated with the company sector; change it only when
                 this zone has different retail dynamics (e.g. a pharmacy
                 endcap inside a DIY store). When the picked value equals
                 the company sector we persist an empty SectorOverride so
                 "no override" stays the data default. -->
            <div
              class="block"
              v-if="detectionZone.type == 'Product Zone' || detectionZone.type == 'Demo Zone' || detectionZone.type == 'Display Zone'"
            >
              <label class="form-label">{{ $t('zoneSettings.settings.sector') }}</label>
              <p class="field-help">
                {{ $t('zoneSettings.settings.sectorHelp') }}
              </p>
              <select class="form-select" v-model="sectorOverrideDisplay">
                <option v-for="opt in sectorOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>

              <div v-if="sectorOverridePreview" class="sector-override-preview">
                {{ $t('zoneSettings.settings.ceilingPreview') }}
                E<sub>max</sub> {{ Math.round(sectorOverridePreview.Emax * 100) }}% ·
                C<sub>max</sub> {{ Math.round(sectorOverridePreview.Cmax * 100) }}%
              </div>
            </div>

            <div class="block block-deviant">
              <label class="form-label">{{ $t('zoneSettings.settings.standStill') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.standStillHelp') }}</p>
              <div class="radio-row">
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="standstill-false"
                    :value="false"
                    v-model="detectionZoneSettings.DetectStandStill"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.common.no') }}</span>
                </label>
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="standstill-true"
                    :value="true"
                    v-model="detectionZoneSettings.DetectStandStill"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.common.yes') }}</span>
                </label>
              </div>

              <div class="block" v-if="detectionZoneSettings.DetectStandStill">
                <div class="slider-container">
                  <Slider
                    class="slider"
                    v-model="detectionZoneSettings.StandStillThreshold"
                    :min="0"
                    :max="300"
                    :step="1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Card: Action Triggers -->
        <div class="subcard" v-if="zoneHasPolygon && !isEntranceLikeZoneType && detectionZone.type !== 'Cash Register Zone'">
          <div class="subcard-head">
            <h6 class="subcard-title" v-if="detectionZone.type === 'Checkout Interaction Zone'">{{ $t('zoneSettings.settings.checkoutTriggers') }}</h6>
            <h6 class="subcard-title" v-else>{{ $t('zoneSettings.settings.behaviorTriggers') }}</h6>
          </div>
          <div class="subcard-body">
            <div class="block">
              <!-- Accurate as of edge 61a78a1c (2026-08-21): the box fires when the
                   customer has been in this zone AND the linked Cash Register Zone has
                   been empty, CONTINUOUSLY, for this many seconds. One number, this
                   field. Before that commit the register was only sampled per-frame and
                   this label overpromised — see docs/reference/notifications_architecture.md §13. -->
              <template v-if="detectionZone.type === 'Checkout Interaction Zone'">
                <label class="form-label">
                  {{ $t('zoneSettings.settings.checkoutTrigger') }}
                </label>
                <p class="field-help">{{ $t('zoneSettings.settings.checkoutTriggerHelp') }}</p>
              </template>
              <template v-else>
                <label class="form-label">
                  {{ $t('zoneSettings.settings.lingerTrigger') }}
                </label>
                <p class="field-help">{{ $t('zoneSettings.settings.lingerTriggerHelp') }}</p>
              </template>

              <div class="radio-row">
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="trigger-false"
                    :value="false"
                    v-model="detectionZoneSettings.TriggerAction"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.common.no') }}</span>
                </label>
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="trigger-true"
                    :value="true"
                    v-model="detectionZoneSettings.TriggerAction"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.common.yes') }}</span>
                </label>
              </div>
            </div>

            <div class="block" v-if="detectionZoneSettings.TriggerAction">
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.TriggerActionThreshold"
                  :min="0"
                  :max="900"
                  :step="5"
                />
              </div>
            </div>

            <div class="block" v-if="detectionZoneSettings.TriggerAction">
              <label class="form-label">{{ $t('zoneSettings.settings.cooldown') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.cooldownHelp') }}</p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.ActionZoneCooldown"
                  :min="0"
                  :max="900"
                  :step="10"
                />
              </div>
            </div>

            <div class="block" v-if="detectionZoneSettings.TriggerAction">
              <label class="form-label">{{ $t('zoneSettings.settings.ackPause') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.ackPauseHelp') }}</p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.ActionAcknowledgePauseDuration"
                  :min="0"
                  :max="1800"
                  :step="30"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Card: Service Response Measurement — docs/reference/service_response_measurement.md.
             Deliberately its OWN card rather than a fourth row in Action Triggers: the
             three settings above decide when staff get alerted, this one decides how we
             MEASURE whether anyone answered. Same units, opposite purpose, and mixing
             them is how "grace" and "cooldown" get confused.
             Product + Demo only — that is phase 1's scope. -->
        <div
          class="subcard"
          v-if="zoneHasPolygon && detectionZoneSettings.TriggerAction &&
                (detectionZone.type === 'Product Zone' || detectionZone.type === 'Demo Zone')"
        >
          <div class="subcard-head">
            <h6 class="subcard-title">{{ $t('zoneSettings.settings.serviceResponse') }}</h6>
          </div>
          <div class="subcard-body">
            <p class="field-help">
              {{ $t('zoneSettings.settings.serviceResponseIntro') }}
            </p>

            <div class="block">
              <label class="form-label">{{ $t('zoneSettings.settings.grace') }}</label>
              <p class="field-help">
                {{ $t('zoneSettings.settings.graceHelpA') }} <em>{{ $t('zoneSettings.settings.graceHelpEm') }}</em>{{ $t('zoneSettings.settings.graceHelpB') }}
              </p>
              <div class="slider-container">
                <Slider
                  class="slider"
                  v-model="detectionZoneSettings.UnattendedGraceSeconds"
                  :min="0"
                  :max="600"
                  :step="10"
                />
              </div>
            </div>

            <!-- The trigger threshold stops being just a notification preference once we
                 measure against it: it IS the store's declared patience bar. Set it very
                 low and almost every shopper who pauses trips it, most of whom drift off
                 before anyone could arrive. -->
            <p class="field-help">
              {{ $t('zoneSettings.settings.serviceStandard') }}
            </p>
            <p
              class="field-help"
              v-if="detectionZoneSettings.TriggerActionThreshold > 0 &&
                    detectionZoneSettings.TriggerActionThreshold < 30"
            >
              {{ $t('zoneSettings.settings.serviceLow') }}
            </p>

            <!-- The cooldown is shared by everyone waiting in the zone (confirmed edge
                 behaviour), so raising it directly reduces how many waits we can observe.
                 Shown only when it actually applies. -->
            <p class="field-help" v-if="detectionZoneSettings.ActionZoneCooldown > 0">
              {{ $t('zoneSettings.settings.serviceCooldown') }}
            </p>
          </div>
        </div>

        <!-- Polygon-level direction picker — only Combined Entrance Exit
             polygons have one; renders here so it sits under "Zone Settings"
             with the rest of the polygon configuration, not under "Tripwire
             Settings" with the per-tripwire pickers. No anchor for the
             polygon row (anchor is a bounding-box-vs-line concept). -->
        <DirectionPicker
          v-for="cfg in zoneLevelDirectionTargets"
          :key="cfg.uid"
          :uid="cfg.uid"
          :title="cfg.title"
          :picker-label="cfg.pickerLabel"
          :picker-tooltip="cfg.pickerTooltip"
          :direction="cfg.direction"
          :show-anchor="false"
        />

        <!-- Group divider: "Tripwire Settings" — symmetric partner of the
             "Zone Settings" divider above; only renders when the zone has at
             least one tripwire. Sits above the geometry preview and the per-
             tripwire direction subcards. -->
        <div class="settings-section-header" v-if="zoneHasTripwires">{{ $t('zoneSettings.settings.tripwireSettings') }}</div>

        <!-- Geometry preview — shown only when the zone has at least one
             tripwire AND we have a snapshot to render against. Lets the user
             visually associate "Tripwire 1" / "Tripwire 2" / … labels in the
             direction blocks below with the actual lines on the camera image
             (the main snapshot is hidden behind this modal). -->
        <div class="zone-preview" v-if="showZonePreview">
          <div class="zone-preview-frame">
            <img
              ref="previewImg"
              :src="capturedFrame"
              :alt="$t('zoneSettings.settings.previewAlt')"
              class="zone-preview-img"
              @load="drawZonePreview"
            />
            <canvas ref="previewCanvas" class="zone-preview-canvas"></canvas>
          </div>
          <p class="zone-preview-caption">
            {{ $t('zoneSettings.settings.previewCaption') }}
          </p>
        </div>

        <!-- Direction Settings — one picker per direction target:
             - Combined EE polygon zones get a zone-level picker (writes to DirectionsCombinedEE).
             - Zones with tripwires get one picker per tripwire (writes to TripwireDirections[id]).
             Both can coexist on a Combined EE zone that also has tripwires.

             Renders BEFORE Dwell Time Estimation because orientation is a
             prerequisite for the dwell analytics — the user thinks about
             "which way is in" before "how long do they stay." -->
        <DirectionPicker
          v-for="cfg in tripwireLevelDirectionTargets"
          :key="cfg.uid"
          :uid="cfg.uid"
          :title="cfg.title"
          :picker-label="cfg.pickerLabel"
          :picker-tooltip="cfg.pickerTooltip"
          :direction="cfg.direction"
          :show-anchor="true"
        />

        <!-- Dwell & Transition Time. Two modes:
              • "dwell"      → Little's Law / XC-MLE on this zone's own in/out
              • "transition" → XC-MLE between a source tripwire and this zone -->
        <div class="subcard" v-if="zoneHasTripwires">
          <div class="subcard-head">
            <h6 class="subcard-title">{{ $t('zoneSettings.settings.dwellTitle') }}</h6>
          </div>
          <div class="subcard-body">
            <div class="block">
              <p class="field-help">{{ $t('zoneSettings.settings.dwellHelp') }}</p>
              <div class="radio-row">
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="dwell-off"
                    :value="false"
                    v-model="detectionZoneSettings.UseTripwireForDwell"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.common.off') }}</span>
                </label>
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="dwell-on"
                    :value="true"
                    v-model="detectionZoneSettings.UseTripwireForDwell"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.common.on') }}</span>
                </label>
              </div>
            </div>

            <!-- Method picker: in/out dwell vs cross-tripwire transition. -->
            <div class="block" v-if="detectionZoneSettings.UseTripwireForDwell">
              <label class="form-label">{{ $t('zoneSettings.settings.method') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.methodHelp') }}</p>
              <div class="radio-row">
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="dwell-mode-dwell"
                    value="dwell"
                    v-model="detectionZoneSettings.DwellCalculationMode"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.settings.modeDwell') }}</span>
                </label>
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="dwell-mode-transition"
                    value="transition"
                    v-model="detectionZoneSettings.DwellCalculationMode"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.settings.modeTransition') }}</span>
                </label>
              </div>
            </div>

            <!-- Transition-mode configuration. Visible only when the user
                 picks "transition". Three knobs:
                   - source tripwire (cross-zone): which tripwire emits the
                     "start" event
                   - source direction: which direction on the source wire
                     counts as the start (in or out)
                   - end direction: which direction on THIS zone's wires
                     counts as the finish (in or out) -->
            <div class="block"
                 v-if="detectionZoneSettings.UseTripwireForDwell
                       && detectionZoneSettings.DwellCalculationMode === 'transition'">
              <label class="form-label">{{ $t('zoneSettings.settings.startLocation') }}</label>
              <p class="field-help">{{ $t('zoneSettings.settings.startLocationHelp') }}</p>
              <div class="radio-row">
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="transition-scope-same"
                    value="same_camera"
                    v-model="transitionSourceScope"
                    @change="onTransitionScopeChange"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.settings.sameCamera') }}</span>
                </label>
                <label class="form-check form-check-inline">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="transition-scope-other"
                    value="other_camera"
                    v-model="transitionSourceScope"
                    @change="onTransitionScopeChange"
                  />
                  <span class="form-check-label">{{ $t('zoneSettings.settings.otherCamera') }}</span>
                </label>
              </div>

              <div class="block" style="margin-top: 0.75rem">
                <label class="form-label" for="transition-source">{{ $t('zoneSettings.settings.startTripwire') }}</label>
                <p class="field-help">{{ $t('zoneSettings.settings.startTripwireHelp') }}</p>
                <select
                  id="transition-source"
                  class="form-select form-select-sm"
                  style="max-width: 24rem"
                  v-model="detectionZoneSettings.TransitionSourceKey"
                >
                  <option :value="null" disabled>{{ $t('zoneSettings.settings.selectTripwire') }}</option>
                  <option
                    v-for="tw in companyTripwires"
                    :key="tw.value"
                    :value="tw.value"
                  >{{ tw.label }}</option>
                </select>
              </div>

              <div class="block" style="margin-top: 0.75rem">
                <label class="form-label">{{ $t('zoneSettings.settings.startDirection') }}</label>
                <p class="field-help">{{ $t('zoneSettings.settings.startDirectionHelp') }}</p>
                <div class="radio-row">
                  <label class="form-check form-check-inline">
                    <input
                      type="radio"
                      class="form-check-input"
                      id="transition-src-in"
                      value="in"
                      v-model="detectionZoneSettings.TransitionSourceDirection"
                    />
                    <span class="form-check-label">{{ startDirInLabel }}</span>
                  </label>
                  <label class="form-check form-check-inline">
                    <input
                      type="radio"
                      class="form-check-input"
                      id="transition-src-out"
                      value="out"
                      v-model="detectionZoneSettings.TransitionSourceDirection"
                    />
                    <span class="form-check-label">{{ startDirOutLabel }}</span>
                  </label>
                </div>
              </div>

              <div class="block" style="margin-top: 0.75rem">
                <label class="form-label">{{ $t('zoneSettings.settings.endDirection') }}</label>
                <p class="field-help">{{ $t('zoneSettings.settings.endDirectionHelp') }}</p>
                <div class="radio-row">
                  <label class="form-check form-check-inline">
                    <input
                      type="radio"
                      class="form-check-input"
                      id="transition-end-in"
                      value="in"
                      v-model="detectionZoneSettings.TransitionEndDirection"
                    />
                    <span class="form-check-label">{{ endDirInLabel }}</span>
                  </label>
                  <label class="form-check form-check-inline">
                    <input
                      type="radio"
                      class="form-check-input"
                      id="transition-end-out"
                      value="out"
                      v-model="detectionZoneSettings.TransitionEndDirection"
                    />
                    <span class="form-check-label">{{ endDirOutLabel }}</span>
                  </label>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      <!-- Modal Footer -->
      <div class="card-footer modal-foot">
        <div class="left-group">
          <div v-if="isSaving" class="saving">
            <div class="saver"></div><span>{{ $t('zoneSettings.common.saving') }}</span>
          </div>

          <button
            type="button"
            class="btn-solid ok"
            :class="{ 'btn-disabled': popUpTrigger !== 'NewZone' && !isSettingsChanged }"
            :disabled="popUpTrigger !== 'NewZone' && !isSettingsChanged"
            @click="saveChanges"
          >
            <i class="bi bi-save"></i>
          </button>

          <button
            type="button"
            class="btn-outline"
            :class="{ 'btn-outline-disabled': popUpTrigger === 'NewZone' }"
            :disabled="popUpTrigger === 'NewZone'"
            @click="closePopup"
          >
            <i class="bi bi-x-square"></i>
          </button>
        </div>

        <button
          class="btn-solid warn"
          :class="{ 'btn-disabled': !isDefaultSettingsChanged }"
          :disabled="!isDefaultSettingsChanged"
          @click="setAllValuesToDefault"
        >
          <i class="bi bi-arrow-counterclockwise"></i> {{ $t('zoneSettings.settings.backToDefault') }}
        </button>
      </div>
    </div>
    <!-- A counting direction flipped: what happened at this spot? (settings step 2e) -->
    <Teleport to="body">
      <ZoneChangeQuestion v-if="zoneQuestion" :key="zoneQuestion.id" :items="zoneQuestion.items"
                          :options="zoneQuestion.options" :title="zoneQuestion.title" :lead="zoneQuestion.lead"
                          @answer="answerZoneQuestion" @cancel="answerZoneQuestion(null)" />
    </Teleport>
  </div>
</template>
  
<script>
import { onMounted, ref, reactive, watch, computed, nextTick } from 'vue'
import { useToast } from 'vue-toastification'

// Sector list + per-sector ceiling lookup for the SectorOverride
// dropdown. Same source of truth as Settings.vue and the math layer
// so we only ever maintain one canonical sector list.
import {
  SECTORS,
  getZoneTypeDefault,
} from '@/prioritization/ceilingsRef'

// Per-zone-type defaults for the three match-time hygiene knobs, mirrored from
// the backend purely so the radio labels can name the value a zone falls back
// to. Display-only — see the header of that module.
import {
  reidMergeDefaultSec,
  maxPlausibleDefaultSec,
  splitMergeDefaultSec,
  formatDurationLabel,
  isMaxPlausibleUnclassified,
  isStaffPositionZoneType,
  ZONE_TYPE_ID_BY_NAME,
} from '@/utils/zoneMatchingDefaults'

import Slider from '@vueform/slider'
import '@vueform/slider/themes/default.css'

import { useDetectionZoneStore } from '../stores/DetectionZone'
import { useCompanyStore } from '@/stores/CompanyStore'
import DirectionPicker from './DirectionPicker.vue'
import ZoneCoachContext from './ZoneCoachContext.vue'
import ZoneChangeQuestion from './zones/ZoneChangeQuestion.vue'
import { directionChanged, matchingChanged, directionLabel, MATCHING_OPTIONS } from '@/zones/changeQuestion'
import { i18n } from '@/i18n'
import { zoneTypeLabel } from '@/i18n/zoneTypes'
import { NAMES as ZONE_NAMES, counterpartTypes, isRole, nameOf } from '@/zoneRoles'

export default {
    name: 'DetectionZoneSettings',

    components: {
        Slider,
        DirectionPicker,
        ZoneCoachContext,
        ZoneChangeQuestion,
    },

    emits: ['close', 'updateZone'],

    props: {
        flowId: Number,
        detectionZone: Object,
        defaultSettings: Object,
        popUpTrigger: String,
        detectionZoneColorsSolid: Object,
        // Snapshot URL (blob: or http:) of the camera frame the zone was drawn
        // on. Used to render a small geometry preview at the top of the modal
        // so the user can visually associate "Tripwire 1" / "Tripwire 2" /…
        // labels in the direction blocks with the actual lines on the image
        // (the main snapshot is hidden behind the modal). Optional — when
        // absent, the preview is hidden.
        capturedFrame: { type: String, default: '' },
    },

    setup(props, { emit }) {
        const toast = useToast()

        const detectionZoneStore = useDetectionZoneStore()
        // Read the company-wide sector so the SectorOverride dropdown
        // can pre-select it (instead of forcing the user to interpret
        // an "optional / leave empty" affordance).
        const companyStore = useCompanyStore()
        const companySector = computed(() =>
            companyStore.company?.CompanySettings?.sector || ''
        )

        const zoneData = reactive({ ...props.detectionZone })
        const detectionZoneColor = ref(props.detectionZoneColorsSolid)

        // Create a deep copy instead of a shallow copy with const detectionZoneSettings = ref({ ...defaultSettings })
        const detectionZoneSettings = ref(JSON.parse(JSON.stringify(props.defaultSettings)))
        const localDefaultSettings = ref(JSON.parse(JSON.stringify(props.defaultSettings)))
        const loadedSettings = ref({})
        const interactionZonesForFlow = ref([])

        const isSaving = ref(null)

        // True once onMounted confirms the zone has a matching row in the backend
        // (i.e. the parent flow has been saved at some point and this zone was persisted).
        // Drives the saveChanges decision: PUT for backend-resident zones, local-only
        // emit('updateZone') otherwise.
        const isInBackend = ref(false)
        // Backend DetectionZoneId once matched — drives the "How Allen sees this zone"
        // context panel (which saves independently via /coach/zone_context).
        const backendZoneId = ref(null)

        const isDefaultSettingsChanged = computed(() => {
            return Object.keys(localDefaultSettings.value).some(key => {
                if (key === "DirectionsCombinedEE") {
                    return JSON.stringify(detectionZoneSettings.value.DirectionsCombinedEE) !== 
                        JSON.stringify(localDefaultSettings.value.DirectionsCombinedEE)
                }
                return detectionZoneSettings.value[key] !== localDefaultSettings.value[key]
            })
        })

        const isSettingsChanged = computed(() => {
            // Iterate union of keys so additions on either side (e.g. seeding
            // TripwireDirections that wasn't in the loaded snapshot) are detected.
            const keys = new Set([
                ...Object.keys(loadedSettings.value || {}),
                ...Object.keys(detectionZoneSettings.value || {}),
            ])
            for (const key of keys) {
                if (key === "DirectionsCombinedEE" || key === "TripwireDirections" || key === "coachContext") {
                    if (JSON.stringify(detectionZoneSettings.value?.[key]) !==
                        JSON.stringify(loadedSettings.value?.[key])) return true
                    continue
                }
                if (detectionZoneSettings.value?.[key] !== loadedSettings.value?.[key]) return true
            }
            return false
        })

        // Polygon-specific settings (overlap mode, dwell duration, action triggers, etc.)
        // only show when the zone actually has a polygon. A tripwire-only zone hides them.
        // The modal only carries the type NAME; the context endpoint wants the id.
        // null for an unknown type, which also keeps the panel off 999 / Cash Register.
        const coachContextZoneTypeId = computed(() =>
            ZONE_TYPE_ID_BY_NAME[props.detectionZone?.type] ?? null)

        const zoneHasPolygon = computed(() => {
            const pts = props.detectionZone?.points
            return Array.isArray(pts) && pts.length >= 3
        })

        // ---------- Sector Override dropdown ----------
        // Mirrors the labels used in Settings.vue (zoneSettings.sectors.*).
        const sectorOptions = computed(() => SECTORS.map(s => ({
            value: s,
            label: i18n.global.te(`zoneSettings.sectors.${s}`) ? i18n.global.t(`zoneSettings.sectors.${s}`) : s,
        })))

        // The dropdown's v-model. Reads from the zone's persisted
        // SectorOverride, but falls back to the company sector when
        // nothing is set — so the picker always shows a real, meaningful
        // value (no "Use company sector (default)" placeholder needed).
        //
        // On write: if the user picks the same sector as the company
        // sector, we persist '' (no override). Otherwise we persist the
        // chosen sector. This keeps the underlying data clean — "no
        // override" stays the natural data default — while the UI never
        // shows a blank/placeholder state.
        const sectorOverrideDisplay = computed({
            get() {
                return detectionZoneSettings.value?.SectorOverride || companySector.value || ''
            },
            set(v) {
                detectionZoneSettings.value.SectorOverride = (v && v === companySector.value) ? '' : (v || '')
            }
        })

        // Live preview of the (Emax, Cmax) this zone's effective sector
        // resolves to. Always rendered when we have a sector to show
        // (which is essentially always now that the dropdown defaults
        // to the company sector).
        const sectorOverridePreview = computed(() => {
            const sec = sectorOverrideDisplay.value
            if (!sec) return null
            const cell = getZoneTypeDefault(sec, 'product')
            return { Emax: cell.Emax.value, Cmax: cell.Cmax.value }
        })

        // Entrance / Exit / Combined EE zones: dwell semantics don't apply, regardless
        // of whether they happen to carry a (legacy) polygon. We hide the polygon-derived
        // settings (precision, dwell, action triggers, buying intent) for these types so
        // the modal only shows what's actually configurable for them: the direction picker.
        const isEntranceLikeZoneType = computed(() => {
            const t = props.detectionZone?.type
            return t === 'Entrance Zone' || t === 'Exit Zone' || t === 'Combined Entrance Exit Zone'
        })

        // ---- Match-time hygiene knobs (default-or-custom) --------------------
        // Three settings the nightly matcher reads out of the settings blob:
        //   SplitVisitMergeThreshold      same TrackId, gap to stitch
        //   ReIDMergeThresholdSec         different TrackId, gap to stitch
        //   MaxPlausibleVisitDurationSec  drop matches longer than this
        // The backend resolves a per-zone-type default whenever a key is ABSENT,
        // so "default" mode is expressed by deleting the key rather than by
        // writing the default's current value. Writing the value would freeze
        // today's default into the zone forever and silently detach it from any
        // future correction — which is exactly the failure mode we're fixing.
        const isCheckoutZoneType = computed(() => {
            const t = props.detectionZone?.type
            return t === 'Checkout Waiting Zone' || t === 'Checkout Interaction Zone'
        })

        const splitMergeDefaultSecValue = computed(() => splitMergeDefaultSec())
        const reidMergeDefaultSecValue = computed(() => reidMergeDefaultSec(props.detectionZone?.type))
        const maxPlausibleDefaultSecValue = computed(() => maxPlausibleDefaultSec(props.detectionZone?.type))

        // English: formatDurationLabel as it always was. Dutch: the same value with
        // "uur", a decimal comma and Dutch words for "no limit" / "off".
        const durationLabel = (sec) => {
            const en = formatDurationLabel(sec)
            if (i18n.global.locale.value === 'en') return en
            if (sec >= 86400) return i18n.global.t('zoneSettings.settings.noLimit')
            if (sec === 0) return i18n.global.t('zoneSettings.settings.durationOff')
            return en.replace('.', ',').replace(/ h$/, ' uur')
        }
        const splitMergeDefaultLabel = computed(() => durationLabel(splitMergeDefaultSecValue.value))
        const reidMergeDefaultLabel = computed(() => durationLabel(reidMergeDefaultSecValue.value))
        const maxPlausibleDefaultLabel = computed(() => durationLabel(maxPlausibleDefaultSecValue.value))
        // "Sitting at 24h" is not one condition but two: a staff position that is
        // uncapped on purpose, and a zone type nobody has classified yet.
        const isStaffPosition = computed(() => isStaffPositionZoneType(props.detectionZone?.type))
        const isMaxPlausibleUnclassifiedType = computed(() =>
            isMaxPlausibleUnclassified(props.detectionZone?.type))

        // A radio bound to key-presence. Selecting "default" deletes the key;
        // selecting "custom" seeds it with the resolved default so the control
        // mounts on the value that was already in force (switching mode alone
        // never changes behaviour — only moving the control afterwards does).
        const overrideMode = (key, defaultSecRef) => computed({
            get: () => (typeof detectionZoneSettings.value?.[key] === 'number' ? 'custom' : 'default'),
            set: (mode) => {
                if (!detectionZoneSettings.value) return
                if (mode === 'custom') {
                    detectionZoneSettings.value[key] = defaultSecRef.value
                } else {
                    delete detectionZoneSettings.value[key]
                }
            },
        })

        const splitMergeMode = overrideMode('SplitVisitMergeThreshold', splitMergeDefaultSecValue)
        const reidMergeMode = overrideMode('ReIDMergeThresholdSec', reidMergeDefaultSecValue)
        const maxPlausibleMode = overrideMode('MaxPlausibleVisitDurationSec', maxPlausibleDefaultSecValue)

        // What the pipeline will actually apply, override or not — drives the
        // "this merges real revisits" warning, which has to fire on the effective
        // value (checkout zones default to 2s without any key being present).
        const reidMergeEffectiveSec = computed(() => {
            const v = detectionZoneSettings.value?.ReIDMergeThresholdSec
            return typeof v === 'number' ? v : reidMergeDefaultSecValue.value
        })

        // Stored in seconds, edited in minutes: the plausibility cap spans 5-60
        // minutes across zone types, a range where a seconds input is unreadable
        // and a seconds slider is too coarse to land on a round value.
        const maxPlausibleMinutes = computed({
            get: () => {
                const v = detectionZoneSettings.value?.MaxPlausibleVisitDurationSec
                if (typeof v !== 'number') return null
                return Math.round(v / 60)
            },
            set: (mins) => {
                if (!detectionZoneSettings.value) return
                const n = Number(mins)
                if (!Number.isFinite(n) || n <= 0) return
                detectionZoneSettings.value.MaxPlausibleVisitDurationSec = Math.round(n) * 60
            },
        })

        // Direction picker labelling per zone type. The DB always stores the configured
        // direction the same way (vertical/horizontal + pattern). Only the user-facing
        // wording changes — the retailer never has to think in terms of `store_in`/`store_out`.
        const directionSectionTitle = computed(() => {
            const t = props.detectionZone?.type
            if (t === 'Entrance Zone' || t === 'Combined Entrance Exit Zone') return i18n.global.t('zoneSettings.settings.dirStoreEntry')
            if (t === 'Exit Zone') return i18n.global.t('zoneSettings.settings.dirStoreExit')
            return i18n.global.t('zoneSettings.settings.dirZoneEntry')
        })
        const directionPickerLabel = computed(() => directionSectionTitle.value)
        const directionPickerTooltip = computed(() => {
            const t = props.detectionZone?.type
            if (t === 'Exit Zone') return i18n.global.t('zoneSettings.settings.tipExit')
            if (t === 'Entrance Zone' || t === 'Combined Entrance Exit Zone') return i18n.global.t('zoneSettings.settings.tipEntry')
            return i18n.global.t('zoneSettings.settings.tipZone')
        })

        // ---- Transition direction labelling --------------------------------
        // The transition timer's Start/End pickers persist the raw values
        // "in" / "out", which map 1:1 to the stored store_in / store_out
        // events. For Entrance/Exit zones that raw vocabulary is a trap: the
        // edge writes the configured "exit direction" into the store_in slot,
        // so on an EXIT zone "in" actually means "customer left the store" and
        // "out" means a wrong-way crossing. We therefore surface the MEANING
        // of each direction for the relevant zone's type rather than "In/Out",
        // exactly as the direction-picker title and Counters pills already do.
        // The stored value is unchanged — this is display-only.
        const zoneKindFromTypeName = (name) => {
            if (name === 'Exit Zone') return 'exit'
            if (name === 'Entrance Zone') return 'entrance'
            if (name === 'Combined Entrance Exit Zone') return 'combined'
            return 'zone'
        }
        // DetectionZoneTypeId enum: 1 Entrance, 2 Exit, 3 Combined EE (rest are
        // polygon/zone-local types). Source tripwires carry the numeric id only.
        const zoneKindFromTypeId = (id) => {
            if (id === 2) return 'exit'
            if (id === 1) return 'entrance'
            if (id === 3) return 'combined'
            return 'zone'
        }
        const directionMeaningLabel = (kind, value) => {
            // value: "in" = the configured store_in direction, "out" = its opposite.
            const t = (key) => i18n.global.t(`zoneSettings.settings.${key}`)
            if (kind === 'exit') {
                return value === 'in' ? t('exitIn') : t('exitOut')
            }
            if (kind === 'entrance') {
                return value === 'in' ? t('entranceIn') : t('entranceOut')
            }
            if (kind === 'combined') {
                return value === 'in' ? t('combinedIn') : t('combinedOut')
            }
            return value === 'in' ? t('zoneIn') : t('zoneOut')
        }

        // End direction = THIS zone's tripwire — its type is known directly.
        const endZoneKind = computed(() => zoneKindFromTypeName(props.detectionZone?.type))
        const endDirInLabel = computed(() => directionMeaningLabel(endZoneKind.value, 'in'))
        const endDirOutLabel = computed(() => directionMeaningLabel(endZoneKind.value, 'out'))

        // Start direction = the SELECTED source tripwire, which belongs to a
        // different zone. Resolve its type via the option list; fall back to
        // raw In/Out until a source is picked.
        const startZoneKind = computed(() => {
            const key = detectionZoneSettings.value?.TransitionSourceKey
            if (!key) return null
            const opt = allCompanyTripwires.value.find(tw => tw.value === key)
            return opt ? zoneKindFromTypeId(opt.zoneTypeId) : null
        })
        const startDirInLabel = computed(() =>
            startZoneKind.value ? directionMeaningLabel(startZoneKind.value, 'in') : i18n.global.t('zoneSettings.settings.rawIn'))
        const startDirOutLabel = computed(() =>
            startZoneKind.value ? directionMeaningLabel(startZoneKind.value, 'out') : i18n.global.t('zoneSettings.settings.rawOut'))

        // ---- Geometry preview (tripwire-labelling) -----------------------
        // Renders a thumbnail of the snapshot the zone was drawn on, with the
        // polygon (faded) and each tripwire (full opacity) overlaid. A numeric
        // badge near each tripwire midpoint matches the "Tripwire N" labels
        // on the direction blocks below, so the user can tell at a glance
        // which physical line each direction block configures.
        const previewImg = ref(null)
        const previewCanvas = ref(null)

        const showZonePreview = computed(() => {
            if (!props.capturedFrame) return false
            const tws = Array.isArray(props.detectionZone?.tripwires) ? props.detectionZone.tripwires : []
            return tws.length > 0
        })

        // Polygon / tripwire internal coordinate format on the prop:
        //   points: [{x, y}, ...]    — both polygon and tripwire endpoints
        // Stored against the camera's native pixel space, so we scale by
        // (clientWidth / naturalWidth) to map onto the rendered <img>.
        const drawZonePreview = () => {
            const img = previewImg.value
            const canvas = previewCanvas.value
            if (!img || !canvas) return
            if (!img.naturalWidth || !img.naturalHeight) return
            canvas.width = img.clientWidth
            canvas.height = img.clientHeight
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            const scaleX = img.clientWidth / img.naturalWidth
            const scaleY = img.clientHeight / img.naturalHeight
            const zone = props.detectionZone || {}
            const color = (props.detectionZoneColorsSolid && props.detectionZoneColorsSolid[zone.type])
                || '#4F7EB3'
            const coord = (pt) => {
                if (Array.isArray(pt)) return { x: pt[0] * scaleX, y: pt[1] * scaleY }
                if (pt && typeof pt === 'object') return { x: (pt.x || 0) * scaleX, y: (pt.y || 0) * scaleY }
                return { x: 0, y: 0 }
            }

            // Polygon (faded background, just for context).
            if (Array.isArray(zone.points) && zone.points.length >= 3) {
                ctx.fillStyle = color + '22'   // ~13% alpha
                ctx.strokeStyle = color + '88' // ~53% alpha
                ctx.lineWidth = 1.5
                ctx.beginPath()
                zone.points.forEach((pt, i) => {
                    const p = coord(pt)
                    if (i === 0) ctx.moveTo(p.x, p.y)
                    else ctx.lineTo(p.x, p.y)
                })
                ctx.closePath()
                ctx.fill()
                ctx.stroke()
            }

            // Tripwires — full opacity, with a numbered badge at the midpoint.
            const tws = Array.isArray(zone.tripwires) ? zone.tripwires : []
            tws.forEach((tw, idx) => {
                if (!tw || !Array.isArray(tw.points) || tw.points.length !== 2) return
                const a = coord(tw.points[0])
                const b = coord(tw.points[1])

                // Line.
                ctx.strokeStyle = color
                ctx.fillStyle = color
                ctx.lineWidth = 3
                ctx.lineCap = 'round'
                ctx.beginPath()
                ctx.moveTo(a.x, a.y)
                ctx.lineTo(b.x, b.y)
                ctx.stroke()

                // Endpoint dots.
                ctx.beginPath()
                ctx.arc(a.x, a.y, 3, 0, Math.PI * 2)
                ctx.fill()
                ctx.beginPath()
                ctx.arc(b.x, b.y, 3, 0, Math.PI * 2)
                ctx.fill()

                // Numbered badge at midpoint. Offset slightly perpendicular to
                // the line so the badge doesn't sit directly on it.
                const midX = (a.x + b.x) / 2
                const midY = (a.y + b.y) / 2
                ctx.fillStyle = '#ffffff'
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.beginPath()
                ctx.arc(midX, midY, 12, 0, Math.PI * 2)
                ctx.fill()
                ctx.stroke()
                ctx.fillStyle = color
                ctx.font = 'bold 14px sans-serif'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText(String(idx + 1), midX, midY)
            })
        }

        // Redraw when the set of tripwires (or zone) on display changes —
        // covers the "modal stays open and zone prop swapped" edge case.
        watch(() => props.detectionZone, () => {
            nextTick(drawZonePreview)
        }, { deep: true })

        // Ensures every tripwire on the zone has a corresponding entry in
        // detectionZoneSettings.TripwireDirections, seeding it from the zone-level
        // DirectionsCombinedEE (or a hardcoded default) when absent. Called once
        // per modal open after detectionZoneSettings has been loaded, so the
        // user sees a sensible initial direction for newly-added tripwires
        // without having to touch the picker before saving.
        const ensureTripwireDirections = () => {
            const tws = Array.isArray(props.detectionZone?.tripwires) ? props.detectionZone.tripwires : []
            if (tws.length === 0) return
            const settings = detectionZoneSettings.value
            if (!settings) return
            if (!settings.TripwireDirections || typeof settings.TripwireDirections !== 'object' || Array.isArray(settings.TripwireDirections)) {
                settings.TripwireDirections = {}
            }
            const seed = (settings.DirectionsCombinedEE && typeof settings.DirectionsCombinedEE === 'object')
                ? settings.DirectionsCombinedEE
                : { vertical: 'down', horizontal: 'right', movementPattern: 'diagonal' }
            tws.forEach(tw => {
                if (!tw || typeof tw.id !== 'string' || !tw.id) return
                const cur = settings.TripwireDirections[tw.id]
                if (!cur || typeof cur !== 'object') {
                    settings.TripwireDirections[tw.id] = JSON.parse(JSON.stringify(seed))
                }
                // Backfill anchor for entries created before the per-tripwire
                // anchor existed. Snapshotted into loadedSettings after seeding,
                // so this is not treated as a user change.
                if (!settings.TripwireDirections[tw.id].anchor) {
                    settings.TripwireDirections[tw.id].anchor = 'foot'
                }
            })
        }

        // List of direction pickers to render in the modal, one entry per
        // "thing that has a direction": the polygon (for Combined EE zones)
        // and each tripwire on the zone. Each entry references the live
        // reactive direction object so the child component's v-model writes
        // propagate back to detectionZoneSettings transparently.
        const directionTargets = computed(() => {
            const out = []
            const zone = props.detectionZone || {}
            const settings = detectionZoneSettings.value
            if (!settings) return out

            // Polygon-level direction — only for Combined EE polygons. The
            // polygon row never gets an anchor picker (anchor is a
            // bounding-box-vs-line concept that only applies to tripwires).
            if (zone.type === 'Combined Entrance Exit Zone'
                && Array.isArray(zone.points) && zone.points.length >= 3
                && settings.DirectionsCombinedEE && typeof settings.DirectionsCombinedEE === 'object') {
                out.push({
                    uid: 'polygon',
                    title: i18n.global.t('zoneSettings.settings.polygonTitle', { title: directionSectionTitle.value }),
                    pickerLabel: directionPickerLabel.value,
                    pickerTooltip: directionPickerTooltip.value,
                    direction: settings.DirectionsCombinedEE,
                    isTripwire: false,
                })
            }

            // Per-tripwire directions, in user-facing order. Each gets the
            // anchor picker rendered inside its DirectionPicker subcard.
            const tws = Array.isArray(zone.tripwires) ? zone.tripwires : []
            if (tws.length > 0 && settings.TripwireDirections && typeof settings.TripwireDirections === 'object') {
                tws.forEach((tw, idx) => {
                    if (!tw || typeof tw.id !== 'string' || !tw.id) return
                    const dir = settings.TripwireDirections[tw.id]
                    if (!dir || typeof dir !== 'object') return
                    out.push({
                        uid: tw.id,
                        title: tws.length > 1
                            ? i18n.global.t('zoneSettings.settings.tripwireTitle', { n: idx + 1, title: directionSectionTitle.value })
                            : directionSectionTitle.value,
                        pickerLabel: directionPickerLabel.value,
                        pickerTooltip: directionPickerTooltip.value,
                        direction: dir,
                        isTripwire: true,
                    })
                })
            }

            return out
        })

        // Split directionTargets so the template can place the polygon's own
        // direction picker (Combined EE) under "Zone Settings" and the per-
        // tripwire pickers under "Tripwire Settings". A direction is "zone-
        // level" when isTripwire is false — only the Combined-EE polygon row.
        const zoneLevelDirectionTargets = computed(() =>
            directionTargets.value.filter(d => !d.isTripwire)
        )
        const tripwireLevelDirectionTargets = computed(() =>
            directionTargets.value.filter(d => d.isTripwire)
        )

        // Local UI state: which scope the "Start tripwire" dropdown lists.
        // "same_camera" = other zones in the SAME flow as the zone being
        // edited; "other_camera" = zones in other flows. Reset on modal
        // open from the loaded source key (see ensureTransitionScope() in
        // the load handler).
        const transitionSourceScope = ref('same_camera')

        // All tripwires across the company, EXCLUDING the zone currently
        // being edited (you can't measure transit from a zone to itself).
        // Each entry's `value` is a stable composite key
        // (flowId::zoneName::tripwireId) that the backend can parse.
        // Kept as the full list — `companyTripwires` below filters it by
        // the scope radio.
        const allCompanyTripwires = computed(() => {
            const out = []
            const zones = detectionZoneStore.detectionzones || []
            const currentZoneName = props.detectionZone?.name
                || props.detectionZone?.DetectionZoneName
            const currentFlowId = props.flowId
            for (const zone of zones) {
                const zoneName = zone.DetectionZoneName || zone.name || '(unnamed)'
                const flowId = zone.FlowId
                if (flowId === currentFlowId && zoneName === currentZoneName) {
                    continue
                }
                let tws = null
                if (Array.isArray(zone.tripwires)) {
                    tws = zone.tripwires
                } else if (zone.DetectionZoneTripwirePoints) {
                    try {
                        tws = typeof zone.DetectionZoneTripwirePoints === 'string'
                            ? JSON.parse(zone.DetectionZoneTripwirePoints)
                            : zone.DetectionZoneTripwirePoints
                    } catch {
                        tws = null
                    }
                }
                if (!Array.isArray(tws) || tws.length === 0) continue
                tws.forEach((tw, idx) => {
                    if (!tw || !tw.id) return
                    out.push({
                        value: `${flowId}::${zoneName}::${tw.id}`,
                        label: i18n.global.t('zoneSettings.settings.tripwireOption', { zone: zoneName, n: idx + 1 }),
                        flowId,
                        zoneName,
                        tripwireId: tw.id,
                        // Carried so the Start-direction picker can label "in"/"out"
                        // by what they MEAN for this source zone's type.
                        zoneTypeId: zone.DetectionZoneTypeId,
                    })
                })
            }
            return out
        })

        // Filtered subset for the dropdown — only tripwires matching the
        // currently-selected scope radio.
        const companyTripwires = computed(() => {
            const currentFlowId = props.flowId
            if (transitionSourceScope.value === 'same_camera') {
                return allCompanyTripwires.value.filter(tw => tw.flowId === currentFlowId)
            }
            return allCompanyTripwires.value.filter(tw => tw.flowId !== currentFlowId)
        })

        // When the user flips the scope radio, clear any previously-picked
        // source key — its options just disappeared from the visible list,
        // so keeping a stale value would silently mislead the dashboard.
        const onTransitionScopeChange = () => {
            detectionZoneSettings.value.TransitionSourceKey = null
        }

        // --- staff-side linking (phase 3) ------------------------------------------
        // The zone's type id, resolved from its NAME because that is what the popup is
        // handed. NAMES is the same map the backend uses.
        const zoneTypeId = computed(() => {
            const name = props.detectionZone?.type
            const hit = Object.keys(ZONE_NAMES).find(k => ZONE_NAMES[k] === name)
            return hit ? Number(hit) : null
        })
        const isStaffZone = computed(() => isRole(zoneTypeId.value, 'staff'))
        const linkedCounterpartName = computed(() => {
            const [t] = counterpartTypes(zoneTypeId.value, 'interaction')
            return t ? nameOf(t) : 'Interaction Zone'
        })
        const linkedZoneLabel = computed(() =>
            i18n.global.t('zoneSettings.settings.linkedLabel', { name: zoneTypeLabel(linkedCounterpartName.value) }))
        const linkedZoneHelp = computed(() => i18n.global.t(
            zoneTypeId.value === 999 ? 'zoneSettings.settings.linkedHelpRegister' : 'zoneSettings.settings.linkedHelpDesk',
            { name: zoneTypeLabel(linkedCounterpartName.value) }))

        // True if the zone carries at least one tripwire — drives the
        // tripwire-only "Track ID Cleanup Rules" section below.
        const zoneHasTripwires = computed(() => {
            const tws = props.detectionZone?.tripwires
            return Array.isArray(tws) && tws.length > 0
        })

        // The edge firmware reads ConnectedInteractionZoneNames (a NAME, scalar). The
        // portal now links by id, so mirror the id back into that field on every
        // change — one control, two representations, neither able to drift from the
        // other. Drop the mirror once the edge reads the id too.
        watch(() => detectionZoneSettings.value.LinkedDetectionZoneId, (id) => {
            const z = interactionZonesForFlow.value.find((x) => x.DetectionZoneId === id)
            detectionZoneSettings.value.ConnectedInteractionZoneNames = z ? z.DetectionZoneName : null
        })

        const fetchInteractionZonesForFlow = async () => {
            const detectionZones = await detectionZoneStore.fetchDetectionZones()
            console.log("[Popup] All fetched zones:", detectionZones)
            const flowFilteredZones = detectionZones.filter(zone => zone.FlowId === props.flowId)
            console.log("[Popup] Zones filtered for flow ID", props.flowId, ":", flowFilteredZones)
            // Only the interaction zones of THIS desk's own service, so an Info Desk
            // can never be paired with a Service zone. The map is mirrored from the
            // backend's zone_roles and kept in step by a test — a drift would pair a
            // desk with the wrong counter and nothing would look broken.
            const wanted = counterpartTypes(zoneTypeId.value, 'interaction')
            interactionZonesForFlow.value = flowFilteredZones.filter(
                zone => wanted.includes(zone.DetectionZoneTypeId))
            console.log("[Popup] Interaction zones found in flow:", interactionZonesForFlow.value)
            return flowFilteredZones
        }

        // Watch for changes in detectionZone and update zoneData accordingly
        watch(() => props.detectionZone, (newZone) => {
            Object.assign(zoneData, newZone)
        }, { deep: true })

        onMounted(async () => {
            console.log("[Popup] onMounted for NEW zone")
            
            nextTick(() => {
                const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
                tooltipTriggerList.forEach((tooltipTriggerEl) => {
                // Use the global bootstrap from the window object
                window.bootstrap?.Tooltip && new window.bootstrap.Tooltip(tooltipTriggerEl)
                })
            })

            const flowFilteredZones = await fetchInteractionZonesForFlow()

            if (props.popUpTrigger === 'ExistingZone') {
                localDefaultSettings.value.DirectionsCombinedEE =
                    props.detectionZone.type === "Combined Entrance Exit Zone"
                        ? { vertical: "down", horizontal: "right", movementPattern: "diagonal" }
                        : { ...props.defaultSettings.DirectionsCombinedEE }

                // Filter by DetectionZoneName (from FlowId-filtered results)
                const matchedZone = flowFilteredZones.find(zone => zone.DetectionZoneName === props.detectionZone.name)
                isInBackend.value = !!matchedZone
                backendZoneId.value = matchedZone?.DetectionZoneId ?? null

                // Pick the source of truth for the settings, in priority order:
                //   1. The zone's settings as the backend currently has them (fully persisted state)
                //   2. The in-memory settings carried on props.detectionZone — important when the
                //      zone has been authored locally and not yet saved to the backend (e.g. a brand
                //      new zone that the user is editing geometry on before the parent flow's Save).
                //   3. The component's defaultSettings as a final fallback.
                let parsed = null
                if (matchedZone?.DetectionZoneSettings) {
                    parsed = JSON.parse(matchedZone.DetectionZoneSettings)
                } else if (props.detectionZone.settings && Object.keys(props.detectionZone.settings).length > 0) {
                    // In-memory settings were already in display-format (percentages 0-100, plain
                    // DirectionsCombinedEE object), so we deep-clone to avoid mutating the prop.
                    parsed = JSON.parse(JSON.stringify(props.detectionZone.settings))
                } else {
                    console.warn("No settings found for the matched zone — falling back to defaults:", matchedZone)
                    parsed = { ...props.defaultSettings }
                }

                // Migrate legacy array format → object format
                if (Array.isArray(parsed.DirectionsCombinedEE)) {
                    const arr = parsed.DirectionsCombinedEE
                    const v = arr.find(d => d === "up" || d === "down") || "down"
                    const h = arr.find(d => d === "left" || d === "right") || "right"
                    parsed.DirectionsCombinedEE = {
                        vertical: v,
                        horizontal: h,
                        movementPattern: "diagonal",
                    }
                }

                // Backfill: a tripwire just added to a zone that previously had no direction config.
                if (!parsed.DirectionsCombinedEE || typeof parsed.DirectionsCombinedEE !== 'object') {
                    parsed.DirectionsCombinedEE = { vertical: 'down', horizontal: 'right', movementPattern: 'diagonal' }
                }

                // The three match-time hygiene knobs are default-or-custom, and
                // "default" is expressed as the key being ABSENT (the backend
                // resolves its own per-zone-type fallback). So there is no
                // backfill here — a missing key is the correct, meaningful state
                // and must survive a round-trip through this modal untouched.
                //
                // One normalisation: zones saved while the old slider existed
                // carry SplitVisitMergeThreshold = 10, which is also the backend
                // default. Drop it so the zone reads as "Default" and re-attaches
                // to the fallback. Behaviour is identical either way (10 = 10) —
                // this only frees the zone to follow the default if it ever
                // changes. An explicitly customised value is left alone.
                if (parsed.SplitVisitMergeThreshold === splitMergeDefaultSec()) {
                    delete parsed.SplitVisitMergeThreshold
                }
                // Retired keys: written by the old "Stay Duration Filters" sliders,
                // never read by any backend code. Strip them on load so a save
                // from this modal cleans the blob; leaving them costs nothing but
                // they mislead anyone reading the JSON.
                delete parsed.MinimumDurationThreshold
                delete parsed.MaximumDurationThresholdSet
                delete parsed.MaximumDurationThreshold

                // Backfill: zones predating the Dwell Group (Mode C) feature.
                // Default off so radio buttons mount selected and the modal
                // doesn't flag this as a user-introduced change.
                if (typeof parsed.UseTripwireForDwell !== 'boolean') {
                    parsed.UseTripwireForDwell = false
                }

                // Backfill: zones predating service-response measurement won't carry
                // the grace yet. 60, NOT 0 — a zero grace would classify every
                // unanswered wait as "no one came" and the "gave up" bucket could
                // never fill. See docs/reference/service_response_measurement.md §6.
                if (typeof parsed.UnattendedGraceSeconds !== 'number') {
                    parsed.UnattendedGraceSeconds = 60
                }

                // Backfill: dwell calculation method picker. "dwell" is the
                // existing in/out behavior; "transition" measures transit
                // time from another tripwire. Default to "dwell" so the
                // modal mounts on the safe (current-behavior) option.
                if (parsed.DwellCalculationMode !== 'transition') {
                    parsed.DwellCalculationMode = 'dwell'
                }
                // Transition-mode supplementary settings. Defaults are null
                // / "in" so the modal mounts cleanly even when the user has
                // never touched the Transition radio.
                if (typeof parsed.TransitionSourceKey !== 'string') {
                    parsed.TransitionSourceKey = null
                }
                // Sanity: a previously-saved TransitionSourceKey may point
                // to the zone we're now editing (e.g. the zone was renamed
                // after the source was picked, or the source was chosen
                // before we filtered self-references out of the dropdown).
                // Reset it — transit from a zone to itself isn't meaningful.
                // Also derives the scope radio from the saved key: if the
                // key references the same flow, set scope to "same_camera";
                // otherwise "other_camera".
                if (typeof parsed.TransitionSourceKey === 'string') {
                    const parts = parsed.TransitionSourceKey.split('::')
                    if (parts.length >= 2) {
                        const srcFlow = Number(parts[0])
                        const srcZone = parts[1]
                        const curZone = props.detectionZone?.name
                            || props.detectionZone?.DetectionZoneName
                        if (srcFlow === props.flowId && srcZone === curZone) {
                            parsed.TransitionSourceKey = null
                        } else {
                            transitionSourceScope.value =
                                srcFlow === props.flowId ? 'same_camera' : 'other_camera'
                        }
                    }
                }
                if (parsed.TransitionSourceDirection !== 'out') {
                    parsed.TransitionSourceDirection = 'in'
                }
                if (parsed.TransitionEndDirection !== 'out') {
                    parsed.TransitionEndDirection = 'in'
                }

                // Convert float → percentage for UI display. Only convert when the value looks
                // like a 0..1 ratio — values already in 0..100 from the in-memory branch are left as-is.
                if (typeof parsed.BottomOverlapPercentage === 'number' && parsed.BottomOverlapPercentage > 0 && parsed.BottomOverlapPercentage <= 1) {
                    parsed.BottomOverlapPercentage = Math.round(parsed.BottomOverlapPercentage * 100)
                }
                if (typeof parsed.BoundingBoxOverlapPercentage === 'number' && parsed.BoundingBoxOverlapPercentage > 0 && parsed.BoundingBoxOverlapPercentage <= 1) {
                    parsed.BoundingBoxOverlapPercentage = Math.round(parsed.BoundingBoxOverlapPercentage * 100)
                }
                if (typeof parsed.EngagersConversionRate === 'number' && parsed.EngagersConversionRate > 0 && parsed.EngagersConversionRate <= 1) {
                    parsed.EngagersConversionRate = Math.round(parsed.EngagersConversionRate * 100)
                }

                detectionZoneSettings.value = JSON.parse(JSON.stringify(parsed))
                // Allen zone-context lives in the same settings blob; ensure the key
                // exists so the panel's v-model + Save round-trips it.
                if (!detectionZoneSettings.value.coachContext) detectionZoneSettings.value.coachContext = {}
                // Seed TripwireDirections from DirectionsCombinedEE for any tripwire
                // that doesn't have an entry yet (e.g. a tripwire freshly added in
                // this editing session, before settings have been persisted).
                ensureTripwireDirections()
                // Snapshot AFTER seeding so the seed isn't flagged as a user change
                // by isSettingsChanged.
                loadedSettings.value = JSON.parse(JSON.stringify(detectionZoneSettings.value))
            }
            else if (props.popUpTrigger === 'NewZone') {
                console.log("[Popup] Setting up settings for new zone")

                detectionZoneSettings.value = {
                    ...props.defaultSettings,
                    DirectionsCombinedEE: props.detectionZone.type === "Combined Entrance Exit Zone"
                        ? { vertical: "down", horizontal: "right", movementPattern: "diagonal" }
                        : { ...props.defaultSettings.DirectionsCombinedEE },
                    BoundingBoxOverlapPercentage: Math.round(props.defaultSettings.BoundingBoxOverlapPercentage * 100),
                    BottomOverlapPercentage: Math.round(props.defaultSettings.BottomOverlapPercentage * 100),
                    EngagersConversionRate: Math.round(props.defaultSettings.EngagersConversionRate * 100),
                }

                // Seed per-tripwire directions before snapshotting so the seeded
                // values are the baseline against which isSettingsChanged compares.
                ensureTripwireDirections()
                // Ensure no changes are detected initially
                loadedSettings.value = JSON.parse(JSON.stringify(detectionZoneSettings.value))
                console.log("[Popup] Initialized newZone settings:", detectionZoneSettings.value)
            }
        })

        const setAllValuesToDefault = () => {
            detectionZoneSettings.value = { 
                ...props.defaultSettings, 
                DirectionsCombinedEE: props.detectionZone.type === "Combined Entrance Exit Zone" 
                    ? { vertical: "down", horizontal: "right", movementPattern: "diagonal" }
                    : { ...props.defaultSettings.DirectionsCombinedEE } // Ensure other types remain unchanged
            }
        }

        const formatDebounceTooltip = value => {
            return value.toFixed(1)
        }

        // The questions a save can ask (settings step 2): a flipped counting direction
        // (corrected | changed | different) and a changed visit-matching setting
        // (from_today | correct_past). Each resolves to the answer, or null when cancelled.
        const zoneQuestion = ref(null)
        const askZone = (changes, extra = {}) => new Promise((resolve) => {
            zoneQuestion.value = {
                id: Date.now(),
                resolve,
                items: [{ oldId: 'zone', title: props.detectionZone.name, changes, suggested: null }],
                ...extra,
            }
        })
        const answerZoneQuestion = (answers) => {
            const q = zoneQuestion.value
            zoneQuestion.value = null
            if (q) q.resolve(answers ? answers.zone : null)
        }

        const saveChanges = async () => {
            const persists = props.popUpTrigger === 'ExistingZone' && isInBackend.value
            let changeKind = null
            let matchChange = null
            if (persists && directionChanged(loadedSettings.value, detectionZoneSettings.value)) {
                changeKind = await askZone([directionLabel()])
                if (!changeKind) return              // cancelled: nothing saved, the window stays
            }
            const matching = persists && changeKind !== 'different'
                ? matchingChanged(loadedSettings.value, detectionZoneSettings.value, props.detectionZone.type) : []
            if (matching.length) {
                matchChange = await askZone(matching, {
                    options: MATCHING_OPTIONS,
                    title: i18n.global.t('zoneSettings.change.matchingTitle'),
                    lead: i18n.global.t('zoneSettings.change.matchingLead'),
                })
                if (!matchChange) return             // cancelled: nothing saved, the window stays
            }
            isSaving.value = true

            // Convert UI-percentage values back to 0-1 ratios for persistence.
            const settingsToSave = JSON.parse(JSON.stringify(detectionZoneSettings.value))
            settingsToSave.BottomOverlapPercentage = settingsToSave.BottomOverlapPercentage / 100
            settingsToSave.BoundingBoxOverlapPercentage = settingsToSave.BoundingBoxOverlapPercentage / 100
            settingsToSave.EngagersConversionRate = settingsToSave.EngagersConversionRate / 100

            // Always update the editor's in-memory state so the latest settings stay visible
            // until the parent flow's Save button finishes the round-trip to the backend.
            emit('updateZone', { ...props.detectionZone, settings: settingsToSave })

            // Persist immediately only when the zone already lives in the backend. For zones
            // that exist only locally (a brand-new zone in this session, or a zone whose parent
            // flow hasn't been saved yet) we skip the PUT — settings will travel with the zone
            // when the parent flow's Save runs.
            if (persists) {
                try {
                    await detectionZoneStore.updateDetectionZoneSettings(
                        props.flowId,
                        props.detectionZone.name,
                        settingsToSave,
                        changeKind,
                        matchChange
                    )
                    loadedSettings.value = JSON.parse(JSON.stringify(detectionZoneSettings.value))
                    // "A different spot now" made it a new zone: the lists must show that one.
                    if (changeKind === 'different') await detectionZoneStore.fetchDetectionZones()
                    toast.success(i18n.global.t('zoneSettings.settings.saved'))
                } catch (error) {
                    console.error("Failed to update detection zone settings", error)
                    toast.error(i18n.global.t('zoneSettings.settings.saveFailed', { error: error.message }))
                    isSaving.value = false
                    return
                }
            }

            isSaving.value = false
            emit('close')
        }

        const closePopup = () => {
            if (isSettingsChanged.value) {
                if (confirm(i18n.global.t('zoneSettings.settings.confirmCancel'))) {
                    setTimeout(() => {
                        detectionZoneSettings.value = loadedSettings.value
                        emit('close')
                    }, 100) // Add a slight delay before navigation
                }
            } else {
                emit('close')
            }
        }

        return {
            zoneQuestion, answerZoneQuestion,
            zoneData,
            backendZoneId,
            coachContextZoneTypeId,
            detectionZoneColor,
            interactionZonesForFlow,
            detectionZoneSettings,
            // Sector override
            sectorOptions,
            sectorOverrideDisplay,
            sectorOverridePreview,
            // ---
            formatDebounceTooltip,
            // Match-time hygiene knobs (default-or-custom)
            isCheckoutZoneType,
            splitMergeMode,
            reidMergeMode,
            maxPlausibleMode,
            splitMergeDefaultLabel,
            reidMergeDefaultLabel,
            maxPlausibleDefaultLabel,
            maxPlausibleDefaultSecValue,
            isStaffPosition,
            isMaxPlausibleUnclassified: isMaxPlausibleUnclassifiedType,
            maxPlausibleMinutes,
            reidMergeEffectiveSec,
            // ---
            isSaving,
            saveChanges,
            isSettingsChanged,
            isDefaultSettingsChanged,
            zoneHasPolygon,
            isEntranceLikeZoneType,
            zoneHasTripwires,
            isStaffZone,
            linkedZoneLabel,
            linkedZoneHelp,
            linkedCounterpartName,
            zoneLevelDirectionTargets,
            tripwireLevelDirectionTargets,
            companyTripwires,
            transitionSourceScope,
            onTransitionScopeChange,
            startDirInLabel,
            startDirOutLabel,
            endDirInLabel,
            endDirOutLabel,
            previewImg,
            previewCanvas,
            showZonePreview,
            drawZonePreview,
            closePopup,
            setAllValuesToDefault,
            zoneTypeLabel,
        }
    }
}
</script>

<style scoped>
/* Sector override preview pill — same modest style as the company
   sector-preview pill in Settings.vue. Shown beneath the dropdown
   when an override is selected. */
.sector-override-preview {
  margin-top: 8px;
  padding: 6px 10px;
  background: #f7faff;
  border: 1px solid #e3ebf7;
  border-radius: 6px;
  font-size: 0.82rem;
  color: #425461;
  display: inline-block;
}

/* Buying-Intent subcard — normalise spacing.
   The global rule (.form-select / .form-control / .input-group-text)
   carries a 36px bottom margin to compensate for the slider's 44px
   tooltip clearance. Inside this subcard the mix of slider + input +
   select that the rule produces stacks unevenly: the slider block
   ends flush at 16px, but the input/select blocks add another 36px
   below their control before the .block margin starts. Reset the
   intra-control bottom margin so .block's own margin-bottom owns the
   gap between every block — consistent 16px between siblings,
   regardless of what control sits inside. */
.subcard-buying-intent .form-select,
.subcard-buying-intent .form-control,
.subcard-buying-intent .input-group-text {
  margin-bottom: 0;
}

/* The standstill setting is structurally different from the three
   economics inputs above it (engagement rate, basket value, sector).
   Give it a bit more breathing room above so it reads as its own
   group — a small visual section break rather than just the next
   item in the same list. */
.subcard-buying-intent .block-deviant {
  margin-top: 18px;
}
/* Modal shell (content only; overlay handled by parent) */
.modal-content{
  display:flex;                 /* NEW */
  flex-direction:column;        /* NEW */
  background:#fff; 
  border-radius:14px; 
  width:min(920px,92vw);
  max-height:86vh; 
  overflow:hidden;              /* moved from auto to hidden */
  box-shadow:0 12px 36px rgba(13,38,76,.18);
  padding:0; 
  z-index:2001;
}

/* Accent spine card */
.kpi-accent{ 
  position:relative; 
  border:none; 
  border-radius:14px; 
  overflow:hidden;
  display:flex;                 /* NEW */
  flex-direction:column;        /* NEW */
  max-height:inherit;           /* NEW */
}
.kpi-accent::before{
  content:""; 
  position:absolute; 
  inset:0 auto 0 0; 
  width:8px; 
  background:var(--accent,#4F7EB3);
}

/* Header */
.modal-head{
  flex:0 0 auto;                /* NEW */
  padding:12px 36px; 
  border-bottom:1px solid #eef3f7; 
  background:#fff;
}
.card-title{
  margin:0; 
  color:#425461; 
  font-weight:800;
}
.dz-name{ color:#2b3a44; font-weight:800 }
.zone-type-line{
  display:flex; 
  align-items:center; 
  gap:8px; 
  margin-top:6px;
}
.zone-dot{
  width:14px; 
  height:14px; 
  border-radius:4px; 
  display:inline-block;
  box-shadow:0 0 0 2px #fff inset, 0 0 0 1px #dfe7ee;
}
.zone-type-text{ color:#6b7a86; font-weight:700 }
.form-label-type{ font-weight:800; color:#425461 }

/* Body (scrollable zone) */
.modal-body{ 
  flex:1 1 auto;                /* NEW - fills remaining space */
  min-height:0;                 /* NEW - necessary for flex scroll */
  overflow-y:auto;              /* NEW - vertical scroll only */
  overscroll-behavior:contain;  /* NEW */
  -webkit-overflow-scrolling:touch; /* NEW smooth scroll on mobile */
  padding:14px 36px; 
  background:#fff 
}

/* Custom scrollbar */
.modal-body::-webkit-scrollbar{ width:10px; }
.modal-body::-webkit-scrollbar-thumb{ background:#cfd8e3; border-radius:8px; }
.modal-body::-webkit-scrollbar-thumb:hover{ background:#b6c2d0; }
.modal-body{ scrollbar-width:thin; scrollbar-color:#cfd8e3 transparent; }

.block{ margin-bottom:16px }

/* Mini-heading inside a subcard. Groups the two merge settings (which are two
   halves of one mechanism, split only by whether the TrackId survived) apart
   from the plausibility cap, without promoting either group to its own card. */
.rule-group-label{
  margin:18px 0 10px;
  padding-bottom:6px;
  border-bottom:1px solid #eef3f7;
  color:#6b7a86;
  font-size:.78rem;
  font-weight:800;
  letter-spacing:.04em;
  text-transform:uppercase;
}
.rule-group-label:first-child{ margin-top:4px }
.divider-soft{ border:0; border-top:1px solid #eef3f7; margin:10px 0 }

/* Subcards (section boxes) */
.subcard{
  border:1px solid #e6edf3;
  border-radius:12px;
  margin-top:24px;
  overflow:hidden;
  box-shadow:0 2px 8px rgba(13,38,76,.05);
}
.subcard-head{
  padding:10px 12px;
  border-bottom:1px solid #eef3f7;
  background:#f9fbfd
}
.subcard-title{ margin:0; color:#4F7EB3; font-weight:800 }
.subcard-body{ padding:12px 12px }

/* Group divider — separates "Zone Settings" subcards from "Tripwire
   Settings" subcards on zones that have both. Sits above the first
   subcard of a group; pulls the subcard's top margin closer so the
   divider visually owns the gap. */
.settings-section-header{
  margin:28px 0 8px 0;
  padding:0 4px 6px 4px;
  font-size:.78rem;
  font-weight:800;
  letter-spacing:0.08em;
  text-transform:uppercase;
  color:#4F7EB3;
  border-bottom:2px solid #e6edf3;
}
.settings-section-header + .subcard{ margin-top:12px }
.settings-section-header:first-child{ margin-top:4px }

/* Labels & inputs */
.form-label{
  display:block;
  margin-bottom:.25rem;
  font-weight:700;
  color:#425461
}
/* Always-visible one-line subtitle under each setting's label. Sized and
   coloured so it recedes visually — returning users skim past it; new
   users read it for free. If a hint genuinely needs more depth, keep it
   here and split off a separate "Learn more" affordance; do not let this
   line grow into a paragraph. */
.field-help{
  margin:-2px 0 8px 0;
  font-size:.78rem;
  line-height:1.35;
  color:#8a99a4;
  font-weight:400;
}
.with-help{ display:inline-flex; align-items:center; gap:6px }
.form-select, .form-control, .input-group-text{
  border:1px solid #dfe7ee; 
  border-radius:10px; 
  padding:8px 10px; 
  color:#2b3a44;
  margin-bottom: 36px;
}
.form-select:focus, .form-control:focus{
  border-color:#4F7EB3; 
  outline:none; 
  box-shadow:0 0 0 3px rgba(79,126,179,.12);
}
.input-group{ display:flex; align-items:center; gap:8px }
.input-group.narrow{ max-width:300px }
.input-group-text{
  background:#f9fbfd; 
  color:#4F7EB3; 
  font-weight:800; 
  border-width:1px;
}

/* Radios */
.radio-row{ 
  display:flex; 
  gap:18px; 
  flex-wrap:wrap; 
  margin-top:6px 
}
.radio-line{ 
  display:flex; 
  align-items:center; 
  gap:14px; 
  flex-wrap:wrap; 
  margin-top:8px 
}
.icon-muted{ color:#425461; opacity:.9 }
.sep{ color:#425461 }

/* Slider */
.slider-container{ width:100% }
.slider{
  margin-top:44px;
  margin-left:10px;
  padding-right:10px;
  --slider-connect-bg:#B2C149;
  --slider-tooltip-bg:#B2C149;
  --slider-height:2px;
  --slider-handle-width:12px;
  --slider-handle-height:12px;
  --slider-tooltip-line-height:1rem;
  --slider-tooltip-font-size:.7rem;
  width:100%;
}

/* Tooltip pill */
.tooltip-icon{
  display:inline-flex; 
  align-items:center; 
  justify-content:center;
  margin-left:6px; 
  width:18px; 
  height:18px; 
  border-radius:50%;
  font-size:11px; 
  font-weight:700; 
  background:#f0f3f6; 
  color:#6b7a86; 
  cursor:help;
}
.tooltip-icon:hover{ background:#e6eef4; color:#425461 }

/* Footer */
.modal-foot{
  flex:0 0 auto;                /* NEW - fixed at bottom */
  display:flex; 
  justify-content:space-between; 
  align-items:center;
  padding:10px 36px; 
  border-top:1px solid #eef3f7; 
  background:#fff;
}
.left-group{ display:flex; align-items:center; gap:10px }
.saving{ display:flex; align-items:center; gap:8px; color:#6b7a86 }

/* Buttons */
.btn-solid, .btn-outline{
  display:inline-flex; 
  align-items:center; 
  gap:8px; 
  cursor:pointer; 
  border-radius:10px;
  padding:8px 12px;
}
.btn-solid{ border:0; color:#fff }
.btn-solid.ok{ background:#6C9046 }
.btn-solid.warn{ background:#f0ad00; color:#1b1b1b }
.btn-solid:disabled, .btn-disabled{ opacity:.55; cursor:not-allowed }

.btn-outline{
  border:1px solid #4F7EB3; 
  background:#fff; 
  color:#4F7EB3;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }
.btn-outline-disabled{ opacity:.55; cursor:not-allowed }

/* Loaders */
.saver{
  width:22px; 
  padding:2px; 
  aspect-ratio:1; 
  border-radius:50%;
  background:#B2C149;
  --_m:conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); 
  mask:var(--_m);
  -webkit-mask-composite:source-out; 
  mask-composite:subtract;
  animation:spinner 1s linear infinite;
}
@keyframes spinner{ to{ transform:rotate(1turn) } }

/* Geometry preview at the top of the modal body — shows the camera snapshot
   with the zone's tripwires overlaid and numbered, so the user can see which
   physical line each "Tripwire N" direction block configures. */
.zone-preview{
  margin: 0 0 16px 0;
  padding: 12px;
  border:1px solid #eef3f7;
  border-radius:12px;
  background:#fafcfe;
}
.zone-preview-frame{
  position:relative;
  display:inline-block;
  max-width:100%;
  line-height:0;     /* removes the gap below the <img> */
}
.zone-preview-img{
  display:block;
  max-width:100%;
  max-height:280px;
  width:auto;
  height:auto;
  border-radius:8px;
}
.zone-preview-canvas{
  position:absolute;
  inset:0;
  pointer-events:none;
  width:100%;
  height:100%;
}
.zone-preview-caption{
  margin: 8px 0 0 0;
  font-size:.8rem;
  color:#7a8a96;
  line-height:1.3;
}
</style>